import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { resolveHref } from '../utils/resolveHref';
import { useCtaColorVars } from '../hooks/useCtaColorVars';
import { onColorLinksCss } from '../utils/onColorLinks';

// ============================================================================
// Slide-in-on-scroll hook
// ============================================================================

// Reveals an element once it scrolls into view, so the feature image can
// slide in from the left. Respects prefers-reduced-motion by revealing
// immediately without animating.
function useSlideInOnScroll<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

// ============================================================================
// Slot group definitions (inline literals — required for schema extraction)
// ============================================================================

// Angled band photo sitting behind the 77% primary overlay — community exteriors
// and outdoor spaces only (not unit interiors).
const backgroundDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      defaultValue: 'Community exterior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a community photo of the property exterior, building facade, aerial/drone overview, entry, grounds, or a real outdoor amenity that exists here (courtyard, pool deck, patio, grilling area, landscaping — only if pictured). Prefer wide outdoor community shots. Skip unit interiors, furnished rooms, bathrooms, laundry, closets, floor-plan diagrams, site maps, and indoor amenity rooms. Do not reuse conveniencecallout.feature_image.',
    context:
      'Full-bleed angled background behind the home-page Convenience Callout, under a heavy primary-color overlay. Must be a different community/exterior photo than conveniencecallout.feature_image.',
  },
};

// Offset feature photo — a distinct community/outdoor shot from the background.
const featureImageDef: SlotGroupDefinition = {
  label: 'Feature Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[4].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[4].alt_text',
      defaultValue: 'Community outdoor space',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a different unused community photo — exterior building, aerial/drone view, entry, grounds, or a real outdoor amenity that exists here (pool, courtyard, patio, grilling, landscaping — only if pictured). Prefer a distinct outdoor subject from the section background. Skip unit interiors, furnished rooms, bathrooms, laundry, closets, floor-plan diagrams, site maps, and indoor amenity rooms. Do not reuse conveniencecallout.background.',
    context:
      'Offset feature photo on the left of the home-page Convenience Callout (desktop); large portrait-style image beside the convenience copy. Must be a different community/exterior photo than conveniencecallout.background.',
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'Button URL',
      type: 'url',
      defaultValue: '/amenities',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: {
      label: 'Button Label',
      type: 'text',
      defaultValue: 'Discover More',
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// ============================================================================
// Component
// ============================================================================

export function ConvenienceCallout() {
  const bg = useSlotGroup('conveniencecallout.background', backgroundDef);
  const feature = useSlotGroup('conveniencecallout.feature_image', featureImageDef);
  const cta = useSlotGroup('conveniencecallout.cta', ctaDef);
  const ctaColorVars = useCtaColorVars();
  const { ref: featureRef, visible: featureVisible } = useSlideInOnScroll<HTMLDivElement>();

  return (
    <section className="relative overflow-hidden bg-background">
      {/* Angled background band with a 77% primary-color overlay. Clip angle
          scales by breakpoint (gentler on small screens) like FloorplansList. */}
      <div className="cc-section-image absolute inset-x-0 top-0 bottom-0">
        <EditableSlotGroup groupPath="conveniencecallout.background" definition={backgroundDef}>
          <div className="absolute inset-0">
            {bg.image ? (
              <VibesitesImage
                url={bg.image}
                alt={bg.alt || ''}
                sizes="100vw"
                className="absolute inset-0 h-full w-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
            )}
            <div className="absolute inset-0 bg-[var(--vibe-color-primary)] opacity-[0.77]" />
          </div>
        </EditableSlotGroup>
      </div>

      {/* Top safe spacer matches the clip inset so content padding above/below
          the band reads as equal; bottom uses the same content pad. */}
      <div className="cc-clip-top-safe relative z-10 w-full shrink-0" aria-hidden="true" />

      {/* Content grid: feature image is desktop-only (hidden below lg) so
          tablet/mobile keep copy + CTA clear of the angled edge — same as
          FloorplansList. */}
      <div className="relative z-10 mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-6 pb-20 md:pb-28 lg:grid-cols-2 lg:gap-16">
        {/* Feature image lifted above the band's top-left angle. Slides in from
            the left once it scrolls into view. Desktop only. */}
        <div
          ref={featureRef}
          className="relative hidden lg:block"
          style={{
            transform: featureVisible ? 'translateX(0)' : 'translateX(-60px)',
            opacity: featureVisible ? 1 : 0,
            transition: 'transform 800ms cubic-bezier(0.22, 1, 0.36, 1), opacity 800ms ease-out',
            willChange: 'transform, opacity',
          }}
        >
          <EditableSlotGroup groupPath="conveniencecallout.feature_image" definition={featureImageDef}>
            {feature.image ? (
              <VibesitesImage
                url={feature.image}
                alt={feature.alt || ''}
                sizes="(min-width: 1024px) 45vw, 90vw"
                className="h-[460px] w-full object-cover object-center shadow-2xl"
              />
            ) : (
              <div className="h-[460px] w-full bg-[var(--vibe-color-surface)] shadow-2xl" />
            )}
          </EditableSlotGroup>
        </div>

        {/* Copy column. */}
        <div className="cc-copy">
          <RichTextContent
            path="conveniencecallout.subtitle"
            label="Subtitle"
            defaultValue="<p>Convenience All Around</p>"
            as="h2"
            className="cc-subtitle-font text-3xl font-normal leading-tight text-white md:text-4xl"
          />

          <RichTextContent
            path="conveniencecallout.body"
            label="Paragraph"
            defaultValue='<p>At <span data-type="field-token" data-field="property_name"></span>, we understand that convenience is the hallmark of true luxury. From our thoughtfully designed common spaces to our attentive concierge services, every amenity is curated to ensure that your lifestyle is both seamless and extraordinary.</p>'
            as="div"
            className="mt-6 max-w-xl text-base leading-relaxed text-primary-foreground opacity-95 md:text-lg"
            style={{ fontFamily: 'var(--vibe-font-body)' }}
          />

          <EditableSlotGroup groupPath="conveniencecallout.cta" definition={ctaDef}>
            <a
              href={resolveHref(cta.url || '/amenities')}
              target={cta._target || '_self'}
              className="mt-8 inline-flex items-center justify-center bg-[var(--cta-bg,var(--vibe-color-secondary))] px-10 py-4 text-sm font-normal uppercase tracking-widest text-[var(--cta-text,var(--vibe-color-text-on-secondary))] shadow-md transition-colors hover:bg-white hover:text-[var(--cta-bg,var(--vibe-color-secondary))]"
              style={{ ...ctaColorVars, fontFamily: 'var(--vibe-font-heading)' }}
            >
              {cta.label || 'Discover More'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        ${onColorLinksCss('.cc-copy [data-vibesite-richtext]')}
        /* Same Oswald display face as other home-page section titles. */
        .cc-subtitle-font,
        .cc-subtitle-font * {
          font-family: var(--vibe-font-heading, 'Oswald', 'Arial Narrow', system-ui, sans-serif);
          font-weight: 400;
        }
        /*
          Angled top band (rises to the right). Gentler on small/tablet
          viewports so more of the photo stays visible; steeper at lg+.
        */
        .cc-section-image {
          clip-path: polygon(0 7%, 100% 0, 100% 100%, 0 100%);
          -webkit-clip-path: polygon(0 7%, 100% 0, 100% 100%, 0 100%);
        }
        @media (min-width: 768px) {
          .cc-section-image {
            clip-path: polygon(0 11%, 100% 0, 100% 100%, 0 100%);
            -webkit-clip-path: polygon(0 11%, 100% 0, 100% 100%, 0 100%);
          }
        }
        @media (min-width: 1024px) {
          .cc-section-image {
            clip-path: polygon(0 16%, 100% 0, 100% 100%, 0 100%);
            -webkit-clip-path: polygon(0 16%, 100% 0, 100% 100%, 0 100%);
          }
        }
        /* Top safe band ≈ clip inset + content pad (matches pb-20 / md:pb-28). */
        .cc-clip-top-safe {
          height: clamp(6.5rem, 14vw, 9rem);
        }
        @media (min-width: 768px) {
          .cc-clip-top-safe {
            height: clamp(8.5rem, 15vw, 12rem);
          }
        }
        @media (min-width: 1024px) {
          .cc-clip-top-safe {
            height: clamp(10rem, 16vw, 14rem);
          }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'ConvenienceCallout',
  displayName: 'Convenience Callout',
  tokens: {
    'conveniencecallout.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Convenience All Around</p>',
    },
    'conveniencecallout.body': {
      label: 'Paragraph',
      type: 'richtext',
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_name"></span>, we understand that convenience is the hallmark of true luxury. From our thoughtfully designed common spaces to our attentive concierge services, every amenity is curated to ensure that your lifestyle is both seamless and extraordinary.</p>',
    },
  },
  slotGroups: {
    'conveniencecallout.background': backgroundDef,
    'conveniencecallout.feature_image': featureImageDef,
    'conveniencecallout.cta': ctaDef,
  },
};

(ConvenienceCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ConvenienceCallout;
